/**
 * Split the Announcements & Gifts products into the page's sections.
 *
 * The page sells several unrelated kinds of thing — keepsake announcements,
 * magnets, gift objects, prints, downloads — and used to list all of them in one
 * flat grid in whatever order the API returned. For a senior that put "Coffee
 * Cup" third, between two graduation announcements, which reads as a page with
 * no opinion about what it is selling.
 *
 * ⚠️ THE SECTION IS DECIDED SERVER-SIDE, NOT HERE. Each add-on arrives carrying
 * `giftCategory` / `giftCategoryLabel` / `giftCategoryPosition`, resolved by
 * Addon#gift_category from the admin-set `category` column with a css_name
 * fallback. This file only groups and orders what it is given — so re-filing a
 * product on /admin/addons moves its tile with no deploy, and a new section
 * added to Addon::GIFT_CATEGORIES appears here with no front-end change. Do not
 * reintroduce a css_name → section map in JavaScript; that is the second copy
 * this design exists to avoid.
 */

/**
 * Marketing copy under each heading. Presentation, so it lives with the view
 * rather than in the database — a section with nothing written for it simply
 * renders its heading, which is why this is a lookup and not a required field.
 */
const SECTION_BLURBS = {
  announcements: 'Share the news — printed on premium card stock, envelopes included.',
  magnets: 'Their portrait on the fridge, where you will actually see it.',
  gifts: 'Their portrait on something you use every day.',
  prints: 'Classic print sizes to frame, share, and hand out.',
  digital: 'A high-resolution download to keep, print, and post yourself.',
};

/**
 * An add-on whose category did not come through still has to appear SOMEWHERE.
 * This is a sales page: the only unacceptable outcome is a product the customer
 * cannot see or buy, so an uncategorised tile collects under a plain heading at
 * the end instead of being filtered away.
 *
 * In practice the server never sends a blank category — Addon#gift_category
 * falls back before serializing. This catches the case the server cannot: a
 * browser holding a bundle from before those attributes were served.
 */
const UNCATEGORISED = {
  key: 'uncategorised',
  label: 'More to Add',
  position: Number.MAX_SAFE_INTEGER,
};

/**
 * Group add-ons into sections, ordered by the server's section position, each
 * section keeping its products in the order they arrived.
 *
 * A section nobody can buy from is not returned at all — an elementary customer
 * sees no "Graduation Announcements" heading, because giftAddonVisible has
 * already dropped every product that would sit under it, and an empty heading
 * advertises something they cannot have.
 */
export default function groupByCategory(addons = []) {
  const sections = new Map();

  addons.forEach((addon) => {
    const key = addon.giftCategory || UNCATEGORISED.key;

    if (!sections.has(key)) {
      sections.set(key, {
        key,
        title: addon.giftCategoryLabel || UNCATEGORISED.label,
        blurb: SECTION_BLURBS[key] || null,
        position: addon.giftCategoryPosition == null
          ? UNCATEGORISED.position
          : addon.giftCategoryPosition,
        addons: [],
      });
    }

    sections.get(key).addons.push(addon);
  });

  return [...sections.values()].sort((a, b) => a.position - b.position);
}
